import { z } from 'zod';
import { assertUnreachable, formatToPascalCase } from '../../../../../react-commons/utils/utilFunctions';
import { ResourceType } from '../../enumerations/ResourceType';
import { VersionStateSchema } from '../../enumerations/VersionState';
import { MutationSuccessResponseSchema } from '../../resources/validation/errorResponseResource';
import { MutationValidationResourceSchema } from '../../resources/validation/mutationValidationResource';
import { patch } from '../APIVersion1';
import { msObject } from '../msObject';
import { CloseVersionMutateObject, CloseVersionMutateObjectSchema } from '../mutateObjects';
import { useMutationWithDelay } from '../useMutationWithDelay';
import { useOnUnauthorized, parseRawData } from './AdapterUtils';
import {showNotification} from "../../../ui/util/showNotification";

const VersionStateChangeResultSchema = msObject({
    successful: z.boolean(),
    changed: z.boolean(),
    fromState: VersionStateSchema,
    toState: VersionStateSchema,
    content: MutationValidationResourceSchema.optional(),
});

export type VersionStateChangeResult = z.infer<typeof VersionStateChangeResultSchema>;

export const useVersionActions = (apiPostfix: string, resourceType: ResourceType) => {
    const onUnauthorized = useOnUnauthorized();

    const useCloseVersion = () => {
        return useMutationWithDelay(
            ({ id }: CloseVersionMutateObject) => patch(`${apiPostfix}/${id}/close`).then(result => processVersionChangeResponse(result, 'close')),
            CloseVersionMutateObjectSchema
        );
    };

    const useLockVersion = () => {
        return useMutationWithDelay(
            ({ id }: CloseVersionMutateObject) => patch(`${apiPostfix}/${id}/lock`).then(result => processVersionChangeResponse(result, 'lock')),
            CloseVersionMutateObjectSchema
        );
    };

    const useUnlockVersion = () => {
        return useMutationWithDelay(
            ({ id }: CloseVersionMutateObject) => patch(`${apiPostfix}/${id}/open`).then(result => processVersionChangeResponse(result, 'open')),
            CloseVersionMutateObjectSchema
        );
    };

    const processVersionChangeResponse = (result: Response, action: 'close' | 'lock' | 'open') => {
        let actionText: string;
        let pastActionText: string;

        switch (action) {
            case 'close':
                actionText = action;
                pastActionText = `${action}d`;
                break;
            case 'lock':
                actionText = action;
                pastActionText = `${action}ed`;
                break;
            case 'open':
                actionText = 'unlock';
                pastActionText = 'unlocked';
                break;
            default:
                return assertUnreachable(action);
        }

        if (result.status === 200) {
            return result
                .json()
                .then(rawData => parseRawData(rawData, VersionStateChangeResultSchema))
                .then(value => {
                    if (value.successful && value.changed) {
                        showNotification({
                            severity: 'success',
                            title: `Successfully ${pastActionText} ${formatToPascalCase(resourceType)}`,
                            validationResourceType: resourceType,
                        });
                    } else if (value.successful && !value.changed) {
                        showNotification({
                            severity: 'warning',
                            title: `${formatToPascalCase(resourceType)} is already ${pastActionText}`,
                            validationResourceType: resourceType,
                        });
                    } else if (!value.successful && !value.changed) {
                        showNotification({
                            severity: 'error',
                            title: `Could not ${actionText} ${formatToPascalCase(resourceType)}`,
                            versionStateChangeResult: value,
                            validationResourceType: resourceType,
                        });
                    }
                    return value;
                });
        } else if (result.status === 401) {
            return onUnauthorized(result, null);
        } else if (result.status === 400) {
            return result
                .json()
                .then(rawData => parseRawData(rawData, MutationSuccessResponseSchema))
                .then(value => {
                    showNotification({
                        severity: 'error',
                        title: `Could not ${actionText} ${formatToPascalCase(resourceType)}`,
                        errorResponseResult: value,
                        validationResourceType: resourceType,
                    });
                })
                .then(_ => null);
        }
        throw Error('Resource could not be created!'); // TODO remove?
    };

    return {
        useCloseVersion,
        useLockVersion,
        useUnlockVersion,
    };
};
